import { Overlay, OverlayConfig } from "@angular/cdk/overlay";
import { CdkPortal, PortalModule } from "@angular/cdk/portal";
import { NgClass } from "@angular/common";
import { Component, effect, ElementRef, inject, input, signal, viewChildren, viewChild } from "@angular/core";
import { Direction, EvDirectivesModule, Gesture } from "ev-directives";
import { EvImageViewerComponent } from "src/app/shared-components/ev-image-viewer/ev-image-viewer.component";
import { AuthImageDirective } from "src/app/shared/directives/auth-image.directive";
import { ImagePackage, ImageViewModel } from "src/models/common/image.model";

@Component({
    selector: "image-carousel",
    templateUrl: "./image-carousel.component.html",
    styleUrls: ["./image-carousel.component.scss"],
    imports: [EvDirectivesModule, NgClass, PortalModule, EvImageViewerComponent, AuthImageDirective],
})
export class ImageCarouselComponent {
    readonly imagePackages = input<ImagePackage[]>();
    readonly estateType = input<string>("");
    readonly useThumbnail = input<boolean>(false);
    readonly imageBorderRadius = input<boolean>(true);

    readonly portal = viewChild(CdkPortal);
    private readonly overlay = inject(Overlay);

    protected activeImage = signal<ImageViewModel>(null);
    protected images = signal<ImageViewModel[]>(null);
    protected imageIndex = signal<number>(0);

    // Lazy loading properties
    private imagesToLoad = new Set<number>(); // Images that should load via authImage

    // ViewChildren to get references to all image elements
    imageElements = viewChildren<ElementRef<HTMLImageElement>>("carouselImg");
    placeholderImage =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB2aWV3Qm94PSIwIDAgMSAxIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=";

    overlayRef: any = null;

    constructor() {
        effect(() => {
            const packages = this.imagePackages();
            const imagePackage = packages?.length > 0 ? this.findFirstPackageWithImages(packages) : null;
            this.images.set(imagePackage?.images || []);
            this.imageIndex.set(0);

            // Clear lazy loading state when images change
            this.imagesToLoad.clear();

            // Immediately load the first image
            if (imagePackage?.images?.length > 0) {
                this.imagesToLoad.add(0);
            }
        });

        // Load current and adjacent images when imageIndex or images change
        effect(() => {
            if (this.images()?.length > 0) {
                this.images(); // Track images signal
                this.imageIndex(); // Track index signal
                this.loadCurrentAndAdjacentImages();
            }
        });
    }

    changeindex(delta: number): void {
        const newindex = this.imageIndex() + delta;
        this.setIndex(newindex);
        // Loading is handled automatically by the effect when imageIndex changes
    }

    private setIndex(index: number): void {
        const insideBounds = Math.max(Math.min(index, this.images().length - 1), 0);
        this.imageIndex.set(insideBounds);
    }

    onGesture(e: Gesture, idx: number): void {
        if (e.direction === Direction.RIGHT) {
            this.changeindex(-1);
        }

        if (e.direction === Direction.LEFT) {
            this.changeindex(1);
        }
    }

    openImage(image: ImageViewModel): void {
        const config = new OverlayConfig({
            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
            width: "100%",
            hasBackdrop: true,
            backdropClass: "cdk-overlay-dark-backdrop",
            panelClass: "cdk-overlay-panel",
        });
        this.activeImage.set(image);
        this.overlayRef = this.overlay.create(config);
        this.overlayRef.attach(this.portal());
        this.overlayRef.backdropClick().subscribe(() => {
            this.overlayRef.detach();
        });
    }

    closeImageViewer(): void {
        this.overlayRef.detach();
    }

    isImageLoaded(index: number): boolean {
        return this.imagesToLoad.has(index);
    }

    shouldLoadImage(index: number): boolean {
        return this.imagesToLoad.has(index);
    }

    getImageUrl(image: ImageViewModel): string {
        return this.useThumbnail() ? image.largeThumbnailUrl : image.imageUrl;
    }

    private loadImageAtIndex(index: number) {
        if (index >= 0 && index < this.images().length && !this.imagesToLoad.has(index)) {
            this.imagesToLoad.add(index);
        }
    }

    private loadCurrentAndAdjacentImages() {
        const currentIndex = this.imageIndex();
        [currentIndex - 1, currentIndex, currentIndex + 1].forEach((index) => {
            this.loadImageAtIndex(index);
        });
    }

    private findFirstPackageWithImages(source: ImagePackage[]): any {
        const pkg = source.find((x) => x.images && x.images.length > 0);
        if (pkg) {
            return pkg;
        } else {
            return source[0];
        }
    }
}
